import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';

export interface IMlsComplianceContext {
  mlsLogoUrls: string[];
  showMlsLegalText: boolean;
}

/** Default MLS compliance context values  */
export const defaultMlsComplianceContext: IMlsComplianceContext = {
  mlsLogoUrls: [],
  showMlsLegalText: false,
};

/** MLS compliance context to store values needed to display on footer */
export const MlsComplianceContext = createContext<IMlsComplianceContext>(
  defaultMlsComplianceContext,
);

/** MLS compliance context hook */
export const useMlsComplianceContext = () => useContext(MlsComplianceContext);

export enum MlsPropertyCardEmitterAction {
  ADD,
  REMOVE,
}

export interface IMlsPropertyCardEmitterContext {
  emitter: ({ logo, action }: { logo: string; action: MlsPropertyCardEmitterAction }) => void;
}

/** Default MLS logos emitter context values  */
const defaultMlsPropertyCardEmitter: IMlsPropertyCardEmitterContext = {
  emitter: () => {},
};

/** MLS logos emitter context to store values needed to display on footer */
const MlsPropertyCardEmitterContext = createContext<IMlsPropertyCardEmitterContext>(
  defaultMlsPropertyCardEmitter,
);

type ReducerState = {
  mlsLogos: Record<string, number>;
  visiblePropertyCards: number;
};

type ReducerAction =
  | { type: 'updateLogos'; value: string; emitterAction: MlsPropertyCardEmitterAction }
  | {
      type: 'updateVisiblePropertyCards';
      emitterAction: MlsPropertyCardEmitterAction;
    };

/** Reducer for MlsComplianceContext */
export const reducer = (state: ReducerState, action: ReducerAction): ReducerState => {
  const changeValue = action.emitterAction === MlsPropertyCardEmitterAction.ADD ? 1 : -1;

  switch (action.type) {
    case 'updateLogos': {
      const { [action.value]: prevLogoCount, ...rest } = state.mlsLogos;
      const newValue = (prevLogoCount || 0) + changeValue;

      if (!action.value || newValue < 1) {
        return {
          ...state,
          mlsLogos: { ...rest },
        };
      }

      return {
        ...state,
        mlsLogos: {
          ...rest,
          [action.value]: newValue,
        },
      };
    }
    case 'updateVisiblePropertyCards':
      return {
        ...state,
        visiblePropertyCards: Math.max(state.visiblePropertyCards + changeValue, 0),
      };
    default:
      return state;
  }
};

interface IMlsComplianceContextProvider extends PropsWithChildren {
  // Used to override any part of the context, generally for testing
  contextOverride?: Partial<IMlsComplianceContext>;
}

/** Context provider for MLS compliance info */
export const MlsComplianceContextProvider: React.FC<IMlsComplianceContextProvider> = ({
  contextOverride,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, { mlsLogos: {}, visiblePropertyCards: 0 });

  const mlsPropertyCardEmitter = useCallback(
    ({ logo, action }: { logo: string; action: MlsPropertyCardEmitterAction }) => {
      dispatch({ type: 'updateLogos', value: logo, emitterAction: action });
      dispatch({ type: 'updateVisiblePropertyCards', emitterAction: action });
    },
    [],
  );

  return (
    <MlsPropertyCardEmitterContext.Provider value={{ emitter: mlsPropertyCardEmitter }}>
      <MlsComplianceContext.Provider
        value={{
          mlsLogoUrls: Object.keys(state.mlsLogos),
          showMlsLegalText: state.visiblePropertyCards > 0,
          ...contextOverride,
        }}
      >
        {children}
      </MlsComplianceContext.Provider>
    </MlsPropertyCardEmitterContext.Provider>
  );
};

/** Used on PropertyCard to know when a property is being displayed and to show mls legal text on footer */
export const useMlsPropertyCardEmitter = (logo: string) => {
  const { emitter } = useContext(MlsPropertyCardEmitterContext);

  useEffect(() => {
    emitter({ logo, action: MlsPropertyCardEmitterAction.ADD });

    return () => {
      emitter({ logo, action: MlsPropertyCardEmitterAction.REMOVE });
    };
  }, [emitter, logo]);
};
